import { useQuery } from "@tanstack/react-query";
import api from "../../lib/api";
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, Cell } from "recharts";
import { ChartLineUp } from "@phosphor-icons/react";

const fmt = (v) => `$${Number(v || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}`;

const Stat = ({ label, value, testid }) => (
  <div className="rounded-md bg-zinc-50 border border-zinc-100 p-2.5" data-testid={testid}>
    <p className="text-[10px] text-zinc-500 uppercase tracking-wide">{label}</p>
    <p className="text-sm font-medium text-zinc-900 font-mono mt-0.5">{value}</p>
  </div>
);

export function PnlAnalytics({ engId }) {
  const { data: a } = useQuery({ queryKey: ["pnl-analytics", engId], queryFn: () => api.get(`/pnl/engagements/${engId}/analytics`).then((r) => r.data) });
  if (!a) return null;
  return (
    <div className="rounded-lg border border-zinc-200 bg-white p-4 space-y-4" data-testid="pnl-analytics">
      <h4 className="text-sm font-medium text-zinc-900 flex items-center gap-1.5"><ChartLineUp size={16} /> Executive Analytics</h4>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
        <Stat label="Revenue" value={fmt(a.revenue)} testid="pnl-metric-revenue" />
        <Stat label="Gross margin" value={a.gross_margin_pct != null ? `${a.gross_margin_pct}%` : "—"} testid="pnl-metric-gross-margin" />
        <Stat label="Net operating margin" value={a.net_operating_margin_pct != null ? `${a.net_operating_margin_pct}%` : "—"} testid="pnl-metric-net-margin" />
        <Stat label="Owner draw ratio" value={a.owner_draw_ratio_pct != null ? `${a.owner_draw_ratio_pct}%` : "—"} testid="pnl-metric-draw-ratio" />
      </div>

      {a.monthly_cash_flow?.length > 0 && (
        <div data-testid="pnl-cashflow-chart">
          <p className="text-[11px] font-medium text-zinc-600 mb-1">Monthly net cash flow</p>
          <ResponsiveContainer width="100%" height={140}>
            <BarChart data={a.monthly_cash_flow} margin={{ top: 4, right: 4, bottom: 0, left: 4 }}>
              <XAxis dataKey="month" tick={{ fontSize: 9 }} tickLine={false} axisLine={{ stroke: "#e4e4e7" }} />
              <YAxis tick={{ fontSize: 9 }} tickLine={false} axisLine={false} width={54} tickFormatter={(v) => `$${(v / 1000).toFixed(1)}k`} />
              <Tooltip formatter={(v) => fmt(v)} contentStyle={{ fontSize: 11 }} />
              <Bar dataKey="net" radius={[3, 3, 0, 0]}>
                {a.monthly_cash_flow.map((m, i) => <Cell key={i} fill={m.net >= 0 ? "#3D6B5E" : "#be123c"} />)}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div data-testid="pnl-top-vendors">
          <p className="text-[11px] font-medium text-zinc-600 mb-1">Top vendors by spend</p>
          {a.top_vendors.map((v) => (
            <div key={v.vendor} className="flex items-center gap-2 py-0.5 text-[11px]">
              <span className="flex-1 truncate text-zinc-700">{v.vendor} <span className="text-zinc-400">×{v.count}</span></span>
              <div className="w-24 h-1.5 rounded-full bg-zinc-100 overflow-hidden"><div className="h-full bg-zinc-800" style={{ width: `${Math.min(v.share_pct, 100)}%` }} /></div>
              <span className="w-24 text-right font-mono text-zinc-600">{fmt(v.total)} · {v.share_pct}%</span>
            </div>
          ))}
        </div>
        <div data-testid="pnl-recurring">
          <p className="text-[11px] font-medium text-zinc-600 mb-1">Recurring charges detected</p>
          {a.recurring.length === 0 && <p className="text-[11px] text-zinc-400 italic">No recurring subscription patterns found.</p>}
          {a.recurring.map((r) => (
            <div key={r.vendor} className="flex items-center justify-between py-0.5 text-[11px]">
              <span className="truncate text-zinc-700">{r.vendor} <span className="text-zinc-400">×{r.charges}</span></span>
              <span className="font-mono text-zinc-600">{fmt(r.avg_amount)}/chg · ~{fmt(r.annualized)}/yr</span>
            </div>
          ))}
        </div>
      </div>
      <p className="text-[9px] text-zinc-400 italic">{a.note}</p>
    </div>
  );
}
